// An image's structure for the library preview (spec docs/specs/web/library-image-structure.md),
// as live's image preview reads it (`usePreviewModalDetails`): read while ⓘ shows the image,
// followed while its analysis runs as a job's page follows a job, and a failed analysis started
// again by Try again.
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../api/errors";
import {
  getAssetAdStructure,
  getGetAssetAdStructureQueryKey,
  getGetJobQueryKey,
  getGetJobQueryOptions,
  useStartJob,
} from "../../api/generated/endpoints/generation/generation";
import type { AdStructure, Job } from "../../api/generated/model";
import { useIdempotencyKey } from "../idempotency";
import { FALLBACK_REFETCH_MS, canChange, jobRefetchInterval } from "../jobs/refetch";
import { useStreamStatus } from "../stream-status";

/** How soon a structure is read again once its job ended: its fill commits just after the job. */
const SETTLE_MS = 1_000;

/** The file's structure, or null before any analysis: the read's 404 is none, not an error. */
async function readStructure(
  teamId: string,
  brandId: string,
  assetId: string,
  signal: AbortSignal,
): Promise<AdStructure | null> {
  try {
    return await getAssetAdStructure(teamId, brandId, assetId, { signal });
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export interface ImageStructureRead {
  /** The file's analysis: undefined until the read answers, null for none. */
  structure: AdStructure | null | undefined;
  /** The read's refusal, shown where the structure goes; null otherwise. */
  error: unknown;
  /** Try again: the analysis of the file started once more. */
  retry: () => void;
  retrying: boolean;
  /** Try again's refusal; null otherwise. */
  retryError: unknown;
}

/** An image's analysis, read only while `enabled` (ⓘ shows an image). */
export function useImageStructure(
  teamId: string,
  brandId: string,
  assetId: string,
  enabled: boolean,
): ImageStructureRead {
  const queryClient = useQueryClient();
  const stream = useStreamStatus();
  const key = getGetAssetAdStructureQueryKey(teamId, brandId, assetId);
  const read = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => readStructure(teamId, brandId, assetId, signal),
    enabled,
    // Under way: read again soon once its job has ended, else as the stream allows.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status !== "in_progress") return false;
      const job = queryClient.getQueryData<Job>(getGetJobQueryKey(teamId, data.job_id));
      if (job !== undefined && !canChange(job.status)) return SETTLE_MS;
      return stream === "live" ? false : FALLBACK_REFETCH_MS;
    },
  });
  // The analysis under way, read as a job's page reads one (the stream's events, else the
  // fallback timer): its end makes the read above settle.
  const running = read.data?.status === "in_progress" ? read.data.job_id : null;
  useQuery({
    ...getGetJobQueryOptions(teamId, running ?? ""),
    enabled: running !== null,
    refetchInterval: (query) => jobRefetchInterval(query.state.data?.status, stream),
  });
  const idempotency = useIdempotencyKey();
  const start = useStartJob({
    mutation: { onSuccess: () => queryClient.invalidateQueries({ queryKey: key }) },
  });
  const retry = () => {
    const data = { type: "image.analysis" as const, source: { kind: "asset" as const, asset_id: assetId } };
    const idempotencyKey = idempotency.keyFor(data);
    start.mutate(
      { teamId, brandId, data, headers: { "Idempotency-Key": idempotencyKey } },
      { onError: (error) => idempotency.failed(idempotencyKey, data, error) },
    );
  };
  return {
    structure: read.data,
    error: read.isError ? read.error : null,
    retry,
    retrying: start.isPending,
    retryError: start.isError ? start.error : null,
  };
}
